import React from 'react';
import Icon from '../../../components/AppIcon';
import RewardBadge from '../../../components/ui/RewardBadge';
import { ACHIEVEMENTS } from '../../../utils/achievementUtils';
import { cn } from '../../../utils/cn';

const AchievementsPanel = ({ familyMembers, memberAchievements, allTasks }) => {
  const getMemberAchievements = (memberId) => {
    return memberAchievements?.[memberId] || [];
  };

  const isAchievementUnlocked = (memberId, achievementId) => {
    const achievements = getMemberAchievements(memberId);
    return achievements?.some(a => a?.id === achievementId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl md:text-2xl font-bold text-foreground">Family Achievements</h2>
        <Icon name="trophy" size={24} className="text-primary" />
      </div>

      {familyMembers?.map(member => {
        const unlockedCount = getMemberAchievements(member?.id)?.length || 0;
        const totalCount = ACHIEVEMENTS?.length;

        return (
          <div key={member?.id} className="bg-card rounded-xl border border-border p-4 md:p-6">
            {/* Member Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                  style={{ backgroundColor: member?.color }}
                >
                  {member?.initials}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{member?.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {unlockedCount} of {totalCount} achievements
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: member?.color }}>
                  {Math?.round((unlockedCount / totalCount) * 100)}%
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-muted rounded-full h-2 mb-4">
              <div 
                className="h-2 rounded-full transition-smooth"
                style={{ 
                  width: `${(unlockedCount / totalCount) * 100}%`,
                  backgroundColor: member?.color
                }}
              />
            </div>

            {/* Achievement Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {ACHIEVEMENTS?.map(achievement => {
                const isUnlocked = isAchievementUnlocked(member?.id, achievement?.id);
                
                return (
                  <div
                    key={achievement?.id}
                    className={cn(
                      'relative p-3 rounded-lg border transition-smooth',
                      isUnlocked 
                        ? 'bg-background border-border hover:shadow-warm-sm' 
                        : 'bg-muted/50 border-muted opacity-60'
                    )}
                  >
                    {/* Achievement Icon */}
                    <div 
                      className={cn(
                        'w-12 h-12 rounded-full flex items-center justify-center mb-2 mx-auto',
                        isUnlocked ? 'animate-pulse' : ''
                      )}
                      style={{ 
                        backgroundColor: isUnlocked ? `${achievement?.color}20` : '#E5E7EB'
                      }}
                    >
                      <Icon 
                        name={achievement?.icon} 
                        size={24} 
                        color={isUnlocked ? achievement?.color : '#9CA3AF'}
                      />
                    </div>

                    {/* Achievement Title */}
                    <h4 className={cn(
                      'text-xs font-medium text-center mb-1',
                      isUnlocked ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                      {achievement?.title}
                    </h4>

                    {/* Reward Badge */}
                    {isUnlocked && achievement?.reward && (
                      <div className="flex justify-center">
                        <RewardBadge reward={achievement?.reward} size="sm" showLabel={false} />
                      </div>
                    )}

                    {/* Lock Icon for Locked Achievements */}
                    {!isUnlocked && (
                      <div className="absolute top-2 right-2">
                        <Icon name="lock" size={12} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AchievementsPanel;
